// Client-side stem persistence for saved sections. Separated stems are normally
// just in-memory blob: URLs that die on reload; this caches the actual stem
// WAV blobs in IndexedDB (same approach as the extension's stem cache) so a
// saved section can be fully re-opened — load its stems back into the engine —
// after a reload, without re-separating.
//
// One record per separation, keyed by a generated `key`. Multiple saved sections
// from the same separation share one record (deduped) via `sectionIds`. A record
// is "pinned" while any saved section references it; unpinned records (recent
// separations the user never saved) are kept only up to MAX_UNPINNED, newest
// first, so the cache can't grow without bound.
import { StemName, STEMS } from './studioEngine';
import type { KeyBpmResult } from './keyBpm';
import type { ChordSegment } from './chordDetect';
import type { BeatGrid } from './beatGrid';

const DB_NAME = 'riffloop-studio-stems';
const STORE = 'separations';
const MAX_UNPINNED = 6;

export interface CachedSeparation {
  key: string;
  stems: Partial<Record<StemName, Blob>>;
  emptyStems: StemName[];
  peaks: number[];
  duration: number;
  sourceRef: string;
  // Human title, so "continue where you left off" can name a separation that
  // has no saved section to borrow a title from. Optional: records written
  // before this field simply don't have it.
  sourceTitle?: string;
  // The original uploaded audio, so a reopened section can restore the full
  // selection picker ("New section" → pick another part) without re-uploading.
  // Optional: sections saved before this existed, or link-sourced ones, won't
  // have it. Stored once per separation key (shared by that key's sections).
  source?: Blob;
  // Detected key + tempo, captured when the original audio was analysed. Kept
  // here because detection needs the decoded source, which a reopened section
  // no longer has in memory — without this a reload showed the 120 BPM default
  // and no key chip at all. Optional: records from before this field, or whose
  // detection hadn't resolved by save time, simply don't have it.
  keyBpm?: KeyBpmResult;
  // Detected chord timeline for the separated window (beta feature). Written
  // after analysis resolves — separations that predate the feature, or whose
  // analysis failed, simply don't have it.
  chords?: ChordSegment[];
  // Beat grid measured off the drums stem (beta feature). Optional for the
  // same reasons; recomputable from the cached stems, unlike chords.
  beatGrid?: BeatGrid;
  createdAt: number;
  sectionIds: string[];
}

export function newSeparationKey(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `sep-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  }
}

let _db: Promise<IDBDatabase> | null = null;
function open(): Promise<IDBDatabase> {
  if (_db) return _db;
  _db = new Promise((resolve, reject) => {
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, 1);
    } catch (e) {
      reject(e);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'key' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  // Best-effort: ask the browser not to evict our storage under disk pressure.
  try {
    navigator.storage?.persist?.();
  } catch {
    /* often denied for a plain origin — harmless */
  }
  return _db;
}

async function getAll(): Promise<CachedSeparation[]> {
  try {
    const db = await open();
    return await new Promise((resolve) => {
      const r = db.transaction(STORE, 'readonly').objectStore(STORE).getAll();
      r.onsuccess = () => resolve((r.result as CachedSeparation[]) || []);
      r.onerror = () => resolve([]);
    });
  } catch {
    return [];
  }
}

export async function getSeparation(key: string): Promise<CachedSeparation | null> {
  try {
    const db = await open();
    return await new Promise((resolve) => {
      const r = db.transaction(STORE, 'readonly').objectStore(STORE).get(key);
      r.onsuccess = () => resolve((r.result as CachedSeparation) || null);
      r.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function putRecord(rec: CachedSeparation): Promise<void> {
  try {
    const db = await open();
    await new Promise<void>((resolve, reject) => {
      const t = db.transaction(STORE, 'readwrite');
      t.objectStore(STORE).put(rec);
      t.oncomplete = () => resolve();
      t.onerror = () => reject(t.error);
    });
  } catch {
    /* best-effort */
  }
}

async function deleteRecord(key: string): Promise<void> {
  try {
    const db = await open();
    await new Promise<void>((resolve) => {
      const t = db.transaction(STORE, 'readwrite');
      t.objectStore(STORE).delete(key);
      t.oncomplete = () => resolve();
      t.onerror = () => resolve();
    });
  } catch {
    /* best-effort */
  }
}

// Store a separation's stems if not already cached. Idempotent per key.
export async function cacheSeparation(
  key: string,
  data: { stems: Partial<Record<StemName, Blob>>; emptyStems: StemName[]; peaks: number[]; duration: number; sourceRef: string; sourceTitle?: string; source?: Blob; keyBpm?: KeyBpmResult },
): Promise<void> {
  const existing = await getSeparation(key);
  if (existing) return;
  // Store the original audio at most once per song (by sourceRef) — trimmed
  // derivatives share the same source, so we don't duplicate a large file.
  let source = data.source;
  if (source) {
    const all = await getAll();
    if (all.some((r) => r.source && r.sourceRef === data.sourceRef)) source = undefined;
  }
  await putRecord({
    key,
    stems: data.stems,
    emptyStems: data.emptyStems,
    peaks: data.peaks,
    duration: data.duration,
    sourceRef: data.sourceRef,
    sourceTitle: data.sourceTitle,
    source,
    keyBpm: data.keyBpm,
    createdAt: Date.now(),
    sectionIds: [],
  });
  await prune();
}

// Attach a (late) detection result to an existing record — the backfill path
// for sections cached before keyBpm was stored, re-analysed on reopen.
export async function setSeparationKeyBpm(key: string, keyBpm: KeyBpmResult): Promise<void> {
  const rec = await getSeparation(key);
  if (!rec) return;
  await putRecord({ ...rec, keyBpm });
}

// Same late-write for the chord timeline. Analysis resolves seconds after the
// mixer opens, i.e. possibly before persistSession has created the record —
// so retry briefly instead of dropping the result on that race.
export async function setSeparationChords(key: string, chords: ChordSegment[]): Promise<void> {
  for (let attempt = 0; attempt < 5; attempt++) {
    const rec = await getSeparation(key);
    if (rec) {
      await putRecord({ ...rec, chords });
      return;
    }
    await new Promise((r) => setTimeout(r, 2000));
  }
}

// Beat grid rides the same record with the same record-may-not-exist-yet retry.
export async function setSeparationBeatGrid(key: string, beatGrid: BeatGrid): Promise<void> {
  for (let attempt = 0; attempt < 5; attempt++) {
    const rec = await getSeparation(key);
    if (rec) {
      await putRecord({ ...rec, beatGrid });
      return;
    }
    await new Promise((r) => setTimeout(r, 2000));
  }
}

// The cached original audio for a song (by sourceRef), from whichever record
// holds it — so any section of that song can restore the selection picker.
export async function getSourceBlob(sourceRef: string): Promise<Blob | null> {
  const all = await getAll();
  const hit = all.find((r) => r.source && r.sourceRef === sourceRef);
  return hit?.source ?? null;
}

// Pin a cached separation to a saved section (keeps it from being evicted).
export async function linkSection(key: string, sectionId: string): Promise<void> {
  const rec = await getSeparation(key);
  if (!rec) return;
  if (!rec.sectionIds.includes(sectionId)) {
    rec.sectionIds.push(sectionId);
    await putRecord(rec);
  }
}

// Drop a section's pin; evict the record if nothing else references it.
export async function unlinkSection(sectionId: string): Promise<void> {
  const all = await getAll();
  for (const rec of all) {
    if (rec.sectionIds.includes(sectionId)) {
      rec.sectionIds = rec.sectionIds.filter((id) => id !== sectionId);
      await putRecord(rec);
    }
  }
  await prune();
}

// sectionId → separation key, for every saved section whose stems are cached.
export async function loadSectionIndex(): Promise<Map<string, string>> {
  const all = await getAll();
  const map = new Map<string, string>();
  for (const rec of all) for (const id of rec.sectionIds) map.set(id, rec.key);
  return map;
}

// Rebuild blob: URLs for a cached separation's stems.
/**
 * Every cached separation, newest first — WITHOUT the blobs. This is the real
 * record of "tracks I split to stems": it exists whether or not a loop was ever
 * saved from the separation, which is what the resume card needs. A cursor
 * rather than getAll() so the stem WAVs are never materialised just to read a
 * timestamp.
 */
export async function listSeparations(): Promise<
  { key: string; sourceRef: string; sourceTitle?: string; createdAt: number; duration: number }[]
> {
  const db = await open();
  return new Promise((resolve) => {
    const out: { key: string; sourceRef: string; sourceTitle?: string; createdAt: number; duration: number }[] = [];
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).openCursor();
    req.onsuccess = () => {
      const cur = req.result;
      if (!cur) { resolve(out.sort((a, b) => b.createdAt - a.createdAt)); return; }
      const r = cur.value as CachedSeparation;
      out.push({ key: r.key, sourceRef: r.sourceRef, sourceTitle: r.sourceTitle, createdAt: r.createdAt || 0, duration: r.duration });
      cur.continue();
    };
    req.onerror = () => resolve([]);
  });
}

export function stemUrlsFrom(rec: CachedSeparation): Record<StemName, string> {
  const urls = {} as Record<StemName, string>;
  for (const s of STEMS) {
    const blob = rec.stems[s];
    if (blob) urls[s] = URL.createObjectURL(blob);
  }
  return urls;
}

async function prune(): Promise<void> {
  const all = await getAll();
  const unpinned = all.filter((r) => r.sectionIds.length === 0).sort((a, b) => b.createdAt - a.createdAt);
  for (const rec of unpinned.slice(MAX_UNPINNED)) await deleteRecord(rec.key);
}
